import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Skeleton,
} from '@mui/material';
import { GetApp, FlashOn, LocalGasStation } from '@mui/icons-material';
import { ethers } from 'ethers';
import { getChainLogoUrl, getTokenLogoUrl } from './ChainLogos';
import WithdrawalModeSelector from './WithdrawalModeSelector';
import RelayStatusTracker from './RelayStatusTracker';
import { estimateFeeBreakdown, estimateSolanaFeeBreakdown, getProtocolFeeBpsFromContract } from '../services/relayService';
import multiTokenTreasuryService from '../services/multiTokenTreasuryService';
import solanaInternullService from '../services/solanaInternullService';

/**
 * Fee breakdown sub-component
 */
const RelayFeeBreakdown = ({ selectedKey, relayInfo, isDark, withdrawalMode, onFeeViability }) => {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedKey || !relayInfo || (withdrawalMode !== 'gasfree' && withdrawalMode !== 'swap')) {
      setBreakdown(null);
      if (onFeeViability) onFeeViability(true);
      return;
    }

    const fetch = async () => {
      setLoading(true);
      try {
        const nativeSymbols = ['ETH', 'BNB', 'TBNB', 'POL', 'MATIC', 'HYPE', 'SOL'];
        const isNative = !selectedKey.token_address ||
          selectedKey.token_address === '0x0000000000000000000000000000000000000000' ||
          nativeSymbols.includes(selectedKey.token_symbol?.toUpperCase());
        const isSolana = selectedKey.chain_name?.toLowerCase().includes('solana') || !!selectedKey.solana_address;

        if (isSolana) {
          try {
            const treasuryState = await solanaInternullService.getTreasuryState();
            if (treasuryState) {
              const bd = estimateSolanaFeeBreakdown(
                treasuryState.protocolFeeBps,
                treasuryState.fixedRelayerFeeLamports,
                selectedKey.denomination_wei,
                selectedKey.denomination,
                selectedKey.token_decimals || 9,
              );
              setBreakdown(bd);
              if (onFeeViability) onFeeViability(bd.viable);
              return;
            }
          } catch (e) {
            console.warn('Failed to read Solana treasury state for fee estimate:', e.message);
          }
          setBreakdown(null);
          if (onFeeViability) onFeeViability(true);
          return;
        }

        let gasPrice = null;
        if (isNative) {
          try {
            const chainId = selectedKey.chain_id || selectedKey.chainId;
            let rpcUrl = multiTokenTreasuryService.getRpcUrlForChain(Number(chainId))
              || multiTokenTreasuryService.getRpcUrlForChain(String(chainId));
            if (!rpcUrl && selectedKey.chain_name) {
              rpcUrl = multiTokenTreasuryService.getRpcUrlByChainName(selectedKey.chain_name);
            }
            if (rpcUrl) {
              const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
              gasPrice = await provider.getGasPrice();
            }
          } catch (e) { /* ignore */ }
        }

        const chainId = selectedKey.chain_id || selectedKey.chainId;
        let effectiveRelayInfo = relayInfo;
        if (chainId) {
          const onChainBps = await getProtocolFeeBpsFromContract(chainId);
          if (onChainBps !== null) {
            effectiveRelayInfo = { ...relayInfo, protocol_fee_bps: onChainBps };
          }
        }

        const bd = estimateFeeBreakdown(
          effectiveRelayInfo,
          selectedKey.denomination_wei,
          selectedKey.denomination,
          isNative,
          gasPrice,
          selectedKey.token_decimals,
          chainId,
        );
        setBreakdown(bd);
        if (onFeeViability) onFeeViability(bd.viable);
      } catch (e) {
        setBreakdown(null);
        if (onFeeViability) onFeeViability(true);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey?.keyIndex, relayInfo, withdrawalMode]);

  if (!breakdown && !loading) return null;

  const tokenSymbol = selectedKey?.token_symbol || 'ETH';
  const subtleText = isDark ? '#888' : '#999';

  return (
    <Box
      sx={{
        p: 1.5,
        mb: 2,
        borderRadius: 2,
        bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        border: '1px solid',
        borderColor: breakdown && !breakdown.viable
          ? (isDark ? 'rgba(244,67,54,0.4)' : 'rgba(244,67,54,0.3)')
          : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'),
      }}
    >
      {loading ? (
        <Box>
          <Skeleton width="60%" height={20} />
          <Skeleton width="80%" height={20} />
          <Skeleton width="50%" height={20} />
        </Box>
      ) : breakdown ? (
        <>
          {breakdown.protocolFeeBps > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: subtleText }}>
                Protocol fee ({breakdown.protocolFeeBps / 100}%)
              </Typography>
              <Typography variant="caption" sx={{ color: subtleText }}>
                -{breakdown.protocolFee} {tokenSymbol}
              </Typography>
            </Box>
          )}
          {breakdown.isSolana && parseFloat(breakdown.relayerFee) > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: subtleText }}>
                Relayer fee (fixed)
              </Typography>
              <Typography variant="caption" sx={{ color: subtleText }}>
                -{breakdown.relayerFee} {tokenSymbol}
              </Typography>
            </Box>
          )}
          {breakdown.isNative && parseFloat(breakdown.gasReimbursement) > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: subtleText }}>
                Gas reimbursement (est.)
              </Typography>
              <Typography variant="caption" sx={{ color: subtleText }}>
                ~{parseFloat(breakdown.gasReimbursement).toFixed(6)} {tokenSymbol}
              </Typography>
            </Box>
          )}
          <Divider sx={{ my: 0.75, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              You receive (est.)
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: !breakdown.viable ? '#f44336' : '#4caf50' }}
            >
              {!breakdown.viable
                ? 'Fees exceed amount'
                : `~${parseFloat(breakdown.netAmount).toFixed(6)} ${tokenSymbol}`
              }
            </Typography>
          </Box>
          {breakdown.viable && parseFloat(breakdown.feePercent) > 0 && (
            <Typography variant="caption" sx={{ color: subtleText, display: 'block', textAlign: 'right', mt: 0.25 }}>
              ~{breakdown.feePercent}% total fees
            </Typography>
          )}
          {!breakdown.viable && (
            <Typography variant="caption" sx={{ color: '#f44336', display: 'block', mt: 0.5 }}>
              This denomination is too small for gas-free relay. Use Direct withdrawal instead.
            </Typography>
          )}
        </>
      ) : null}
    </Box>
  );
};

const WithdrawalDialog = ({
  open,
  onClose,
  withdrawDeposit,
  availableKeys,
  selectedKeyIndex,
  onSelectKey,
  recipientAddress,
  onRecipientChange,
  withdrawalMode,
  onWithdrawalModeChange,
  relayInfo,
  relayerHasBalance = true,
  withdrawing,
  onExecuteWithdrawal,
  relayJobId,
  relayNodeUrl,
  // Keep these props for backward compat even though we don't render the buttons
  expandedChains,
  expandedTokens,
  onToggleChain,
  onToggleToken,
  onDownloadKeys,
  onUploadKeys,
  isDark,
  isMobile,
  account,
  solanaPublicKey,
  solanaConnected,
}) => {
  const selKey = availableKeys.find(k => k.keyIndex === selectedKeyIndex);
  const isSolanaKey = selKey?.chain_name?.toLowerCase().includes('solana') || !!selKey?.solana_address;
  const relayAvailable = !!relayInfo && relayerHasBalance;

  return (
    <Dialog
      open={open}
      onClose={withdrawing ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: isDark ? '#1a1a1a' : '#fff',
          backgroundImage: 'none',
          borderRadius: isMobile ? 0 : 3,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Unlock Funds</Typography>
      </DialogTitle>
      <DialogContent>
        {/* Key selector */}
        <FormControl fullWidth sx={{ mb: 2, mt: 1 }} size="small">
          <InputLabel>Select Key</InputLabel>
          <Select
            value={selectedKeyIndex !== null ? selectedKeyIndex : ''}
            onChange={(e) => onSelectKey(e.target.value === '' ? null : parseInt(e.target.value))}
            label="Select Key"
            disabled={withdrawing}
          >
            {availableKeys.map((key) => (
              <MenuItem key={key.keyIndex} value={key.keyIndex} disabled={key.isUsed}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  {getChainLogoUrl(key.chain_name) && (
                    <Box
                      component="img"
                      src={getChainLogoUrl(key.chain_name)}
                      alt={key.chain_name}
                      sx={{ width: 20, height: 20, borderRadius: '50%' }}
                    />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, opacity: key.isUsed ? 0.5 : 1 }}>
                      {key.denomination} {key.token_symbol || 'ETH'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                      {key.chain_name || 'localhost'}
                    </Typography>
                  </Box>
                  <Chip
                    label={key.isUsed ? 'Used' : 'Available'}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      bgcolor: key.isUsed ? 'rgba(244,67,54,0.15)' : 'rgba(34,197,94,0.15)',
                      color: key.isUsed ? '#f44336' : '#22c55e',
                    }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Selected key summary */}
        {selKey && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 1.5,
              mb: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: isDark ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.4)',
              bgcolor: isDark ? 'rgba(34,197,94,0.05)' : 'rgba(34,197,94,0.08)',
            }}
          >
            {getChainLogoUrl(selKey.chain_name) && (
              <Box
                component="img"
                src={getChainLogoUrl(selKey.chain_name)}
                alt={selKey.chain_name}
                sx={{ width: 32, height: 32, borderRadius: '50%' }}
              />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {selKey.denomination} {selKey.token_symbol || 'ETH'}
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666' }}>
                on {selKey.chain_name?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'localhost'}
              </Typography>
            </Box>
            <Chip
              icon={withdrawalMode === 'gasfree' && relayAvailable ? <FlashOn fontSize="small" /> : <LocalGasStation fontSize="small" />}
              label={withdrawalMode === 'gasfree' && relayAvailable ? 'Gas-Free' : 'Direct'}
              size="small"
              sx={{
                bgcolor: withdrawalMode === 'gasfree' && relayAvailable ? 'rgba(76,175,80,0.15)' : 'rgba(255,255,255,0.08)',
                color: withdrawalMode === 'gasfree' && relayAvailable ? '#4caf50' : (isDark ? '#aaa' : '#666'),
                fontWeight: 600,
              }}
            />
          </Box>
        )}

        {/* Withdrawal Mode */}
        <WithdrawalModeSelector
          mode={withdrawalMode}
          onChange={onWithdrawalModeChange}
          relayerAvailable={!!relayInfo}
          disabled={withdrawing}
        />

        {/* Fee breakdown */}
        {selKey && relayInfo && (
          <RelayFeeBreakdown
            selectedKey={selKey}
            relayInfo={relayInfo}
            isDark={isDark}
            withdrawalMode={withdrawalMode}
          />
        )}

        {/* Relayer balance warning */}
        {withdrawalMode === 'gasfree' && relayInfo && !relayerHasBalance && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Relayer has no balance. Switch to Direct instead.
          </Alert>
        )}

        {/* Direct mode: wallet info */}
        {!isSolanaKey && withdrawalMode === 'direct' && !withdrawing && (
          <Typography variant="caption" sx={{ color: isDark ? '#888' : '#999', display: 'block', mb: 1 }}>
            Signing wallet: {account}
          </Typography>
        )}

        {isSolanaKey && withdrawalMode === 'direct' && !solanaConnected && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Connect your Phantom wallet to unlock from Solana.
          </Alert>
        )}

        {/* Recipient Address */}
        {!withdrawing && (
          <TextField
            fullWidth
            size="small"
            label={isSolanaKey ? 'Recipient Solana Address' : 'Recipient Address'}
            value={recipientAddress}
            onChange={(e) => onRecipientChange(e.target.value)}
            placeholder={isSolanaKey ? 'Base58 Solana address...' : '0x...'}
            helperText={
              withdrawalMode === 'gasfree'
                ? 'Relayer delivers funds — no gas needed in this wallet.'
                : 'Use a different address from where you deposited for privacy.'
            }
            sx={{ mb: 2 }}
            disabled={withdrawing}
          />
        )}

        {/* Withdrawal progress */}
        {withdrawing && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {relayJobId ? (
              <RelayStatusTracker
                jobId={relayJobId}
                nodeUrl={relayNodeUrl}
                isDark={isDark}
              />
            ) : (
              <>
                <CircularProgress size={32} sx={{ color: '#22c55e', mb: 1.5 }} />
                <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#666' }}>
                  {withdrawalMode === 'gasfree' ? 'Signing and submitting to relayer...' : 'Processing unlock...'}
                </Typography>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={withdrawing} sx={{ color: isDark ? '#aaa' : '#666' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onExecuteWithdrawal}
          disabled={withdrawing || selectedKeyIndex === null}
          startIcon={withdrawing ? <CircularProgress size={16} /> : <GetApp />}
          sx={{
            background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
            '&:hover': { background: 'linear-gradient(90deg, #16a34a 0%, #22c55e 100%)' },
            '&.Mui-disabled': { background: isDark ? '#333' : '#ccc' },
            px: 3,
          }}
        >
          {withdrawing
            ? (withdrawalMode === 'gasfree' ? 'Relaying...' : 'Unlocking...')
            : (withdrawalMode === 'gasfree' ? 'Unlock (Gas-Free)' : 'Unlock to Wallet')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WithdrawalDialog;
